import { useEffect, useState } from "react";
import { useLocation as useRouterLocation } from "react-router-dom";

import { useAuth } from "@/contexts/AuthContext";
import { usePermissions } from "@/hooks/usePermissions";
import {
  useWhatsAppAiAddon,
  useConversationsPendingCount,
} from "@/hooks/useWhatsAppFloating";
import ConversationsPopup from "./ConversationsPopup";

/**
 * Bola flotante de CONVERSACIONES (14-sep-2026, petición de Luis): para los
 * negocios CON WhatsApp IA contratado, acceso de pulgar abajo a la derecha
 * en todas las pantallas, con el contador de conversaciones que esperan a
 * una persona. Cuando esta bola está presente, la de ayuda se hace pequeña
 * y se va abajo a la izquierda (FloatingHelpButton usa las mismas
 * condiciones); sin WhatsApp IA esta bola no existe y la ayuda se queda en
 * su sitio de siempre.
 *
 * Superadmin (18-sep-2026): la bola es SUYA — habla con el inbox de soporte
 * de plataforma (hosteleros escribiendo a ReservaManager), no con el del
 * negocio que tenga seleccionado en ese momento, y no depende del add-on de
 * WhatsApp IA (eso es cosa de los negocios).
 */
const HIDDEN_PATHS = ["/conversations", "/admin/soporte"];

/** Logo de WhatsApp (lucide no lo trae; path estándar de simple-icons). */
function WhatsAppIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
    </svg>
  );
}

export default function FloatingConversationsButton() {
  const routerLocation = useRouterLocation();
  const { user } = useAuth();
  const { can } = usePermissions();
  // El popup se abre SOBRE la bola (como el chat de ayuda): revisas o
  // contestas y sigues con lo que estabas. Sin cierre por clic fuera, para
  // no perder una respuesta a medio escribir.
  const [popupOpen, setPopupOpen] = useState(false);

  // AL NAVEGAR, EL POPUP SE APARTA (20-sep-2026, reportado por Luis). Vive en
  // el Layout, fuera del <Outlet>, así que sobrevivía a los cambios de página:
  // tocabas una notificación de la campana, llegabas a la reserva… y te la
  // encontrabas tapada por el chat y con la página CONGELADA, porque mientras
  // el popup está abierto bloquea a propósito el scroll del <main>. Ibas a un
  // sitio y no podías ni mirarlo.
  //
  // Lo que estuvieras escribiendo NO se pierde: el borrador se guarda por
  // conversación y vuelve solo al reabrir (ver ConversationsPopup).
  const currentPath = routerLocation.pathname;
  useEffect(() => {
    setPopupOpen(false);
  }, [currentPath]);

  // Superadmin (18-sep-2026, petición de Luis): "si estoy logueado como
  // superadmin, la bola me debe enseñar el superadmin y no la del local en
  // cuestión" — aunque siga dentro de un negocio concreto. Mismo criterio de
  // rol que Sidebar.tsx/AdminRoute.tsx (`user.role === "owner"`). El acceso a
  // las conversaciones del negocio que esté mirando sigue disponible por el
  // icono del navbar y el ítem "Conversaciones" del menú lateral del propio
  // negocio (ninguno de los dos depende de esta bola).
  const isSuperAdmin = user?.role === "owner";
  const basePath = isSuperAdmin ? "/admin/platform-conversations" : "/whatsapp-conversations";

  const allowed = can("conversaciones:gestionar");
  const hasAddon = useWhatsAppAiAddon();
  // El add-on de WhatsApp IA es de los NEGOCIOS: el superadmin ve su propia
  // bola de soporte sin depender de tenerlo contratado.
  const visible =
    allowed &&
    (isSuperAdmin || hasAddon === true) &&
    !HIDDEN_PATHS.some((p) => routerLocation.pathname.startsWith(p));
  const { unread: count } = useConversationsPendingCount(visible, basePath);

  if (!visible) return null;

  return (
    <div className="fixed bottom-10 sm:bottom-5 right-5 z-40">
      {popupOpen && (
        <ConversationsPopup onClose={() => setPopupOpen(false)} isPlatform={isSuperAdmin} />
      )}
      {/* La bola se OCULTA con el popup abierto: en móvil quedaba pegada al
          botón de enviar y provocaba cierres accidentales (14-sep-2026). El
          cierre vive en la ✕ de la cabecera del popup. */}
      {!popupOpen && (
        <button
          onClick={() => setPopupOpen(true)}
          title="Conversaciones"
          aria-label={
            count > 0 ? `Conversaciones: ${count} sin leer` : "Conversaciones"
          }
          className="relative w-14 h-14 rounded-full bg-emerald-500 shadow-lg flex items-center justify-center text-white hover:bg-emerald-600 hover:shadow-xl transition-all"
        >
          <WhatsAppIcon className="w-7 h-7" />
          {count > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[22px] h-[22px] px-1 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center border-2 border-white">
              {count > 9 ? "9+" : count}
            </span>
          )}
        </button>
      )}
    </div>
  );
}
